import { Component, EventEmitter, Input, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { CustomerService } from "src/app/shared/services/customer.service";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { UserRoles } from "src/app/shared/enums/user-roles";

function checkRequiredFields(
  control: AbstractControl,
): ValidationErrors | null {
  const keys = Object.keys(control.value);
  const requiredKeys = keys.filter(
    key => (control.get(key)?.errors as any)?.required,
  );

  return requiredKeys.length === 0 ? null : { requiredFields: true };
}
@Component({
  selector: "app-customer-form",
  templateUrl: "./customer-form.component.html",
  standalone: false,
})
export class CustomerFormComponent {
  @Input() contractId!: number;
  @Input() customerId!: number;

  @Output() apiKey: EventEmitter<string> = new EventEmitter<string>();

  isLoading = false;
  isEditMode = false;

  userSettingsForm!: FormGroup;
  userRole = UserRoles;

  preEditedState: any;

  constructor(
    private fb: FormBuilder,
    public translate: TranslateService,
    private customerService: CustomerService,
  ) {}

  ngOnInit() {
    this.userSettingsForm = this.fb.group(
      {
        companyName: ["", Validators.required],
        name: ["", Validators.required],
        surname: ["", Validators.required],
        email: ["", [Validators.required, Validators.email]],
        phone: ["", Validators.required],
        address: ["", Validators.required],
        pib: ["", Validators.required],
        mb: ["", Validators.required],
      },
      { validator: checkRequiredFields },
    );

    this.loadData();
    this.userSettingsForm.disable();
  }

  loadData() {
    const getContractSubscription = this.customerService
      .getCustomer(this.customerId)
      .subscribe((res) => {
        getContractSubscription.unsubscribe();
        this.userSettingsForm.patchValue(res);
        this.apiKey.emit(res.apiKey);
      });
  }

  updateUser() {
    if (this.userSettingsForm.valid) {
      this.isLoading = true;
      const updateSubscription = this.customerService.updateCustomer(this.customerId, this.userSettingsForm.value).subscribe({
        next: () => {
          updateSubscription.unsubscribe();
          this.isLoading = false;
          this.isEditMode = false;
          this.userSettingsForm.markAsUntouched();
          this.userSettingsForm.disable();
          this.loadData();
          showMessage(PopupType.Success, this.translate.instant("profile_updated_succesfully"));
        },
        error: (error) => {
          updateSubscription.unsubscribe();
          this.isLoading = false;
          showMessage(PopupType.Danger, error);
        },
      });
    }
    else {
      let message = "";

      let invalidKeys = "";
      Object.keys(this.userSettingsForm.controls).forEach((key) => {
        const fieldHasError
          = this.userSettingsForm.get(key)?.errors?.["required"];

        if (fieldHasError) {
          const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

          invalidKeys += ` ${capitalizedKey}`;
        }
      });
      if (invalidKeys.length < 2) {
        message = `${this.translate.instant(
          "profile_field_invalid",
        )} ${invalidKeys}`;
      }
      else {
        message = `${this.translate.instant(
          "profile_fields_invalid",
        )} ${invalidKeys}`;
      }
      showMessage(PopupType.Danger, message);
    }
  }

  setEditMode() {
    this.preEditedState = this.userSettingsForm.value;
    this.userSettingsForm.enable();
    this.isEditMode = true;
  }

  cancelEdit() {
    this.isEditMode = false;
    this.userSettingsForm.disable();
    this.userSettingsForm.patchValue(this.preEditedState);
  }
}
